import { Bars3Icon, MagnifyingGlassPlusIcon, PlusIcon } from '@heroicons/react/24/outline';
import { roundNumber } from '../../utils/functions/calc.function';
import Button from '../button/Button';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { InvoiceItem } from '../../types/Invoice';
import { RowData } from './InvoiceItemTable.types';
import { VatCode } from '../../types/VatCode';
import SelectFilteredModalTable from '../select/SelectFilteredModalTable';
import { useTranslation } from '../../context/translationContext';
import { EntityType } from '../../types/Files';
import { useEffect, useRef, useState } from 'react';
import { columns } from '../columns/Company/VatCodesColumn';
import { columns as codeBookColumns } from '../columns/Company/InvoiceCodeBookItemColumns';
import { CodeBookItem } from '../../types/CodeBookItem';
import { Currency } from '../../types/Currency';
import { Modal } from '../modal/modal';
import { format } from 'date-fns';
import { dateSubmitFormat } from '../../types/core/date.types';
import http from '../../api/axios';
import { apiUrl } from '../../api/apiUrl';
import { useLoading } from '../../context/loadingContext';
import { DateField } from '../form/input/DateField';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { formatPrice } from '../../utils/functions/data.functions';
import SelectAutocompleteTable, { RowHeading } from '../select/SelectAutocompleteTable';
import { Loader } from '../loader/Loader';

interface Props {
  items: RowData[];
  onItemsChange: (newItems: RowData[]) => void;
  rounding: number;
  itemHints?: any[];
  setItemHints?: (newHints: any[]) => void;
  onChangeNameQuery: (k: string) => void;
  isReadOnly?: boolean;
  vatCodes?: VatCode[];
  codeBookItems?: CodeBookItem[];
  codeBookItemsUseCode: boolean;
  invoiceCurrency: Currency;
  isPrint?: boolean;
  maxDecimalNumUnitPrice?: number | null;
}

const enum ModalEntityOption {
  VAT_CODES = 'vat-codes',
  ITEMS = 'items',
}

export default function InvoiceItemTable({
  items,
  itemHints,
  setItemHints,
  onChangeNameQuery,
  onItemsChange,
  rounding,
  isReadOnly,
  vatCodes,
  codeBookItems,
  codeBookItemsUseCode,
  invoiceCurrency,
  isPrint,
  maxDecimalNumUnitPrice,
}: Props) {
  const { t } = useTranslation();
  const { startLoading, stopLoading } = useLoading();
  const [isTableOpen, setIsTableOpen] = useState<boolean>(false);
  // hide column if all items have empty code
  const [codeBookItemsOpen, setCodeBookItemsOpen] = useState<boolean>(false);
  // hide column if user deselected option in company setting
  const [modalDiffCurrencyOpen, setModalDiffCurrencyOpen] = useState<boolean>(false);
  const [active, setActive] = useState<boolean>(false);
  const [itemCurrencyRatesDate, setItemCurrencyRatesDate] = useState<Date | null>(new Date());
  const [selectedItem, setSelectedItem] = useState<InvoiceItem | null>();
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [colNameStyle, setColNameStyle] = useState<string>(``);
  const tableRef = useRef<HTMLTableElement>(null);
  const [selectedIndex, setSelectedIndex] = useState<number | undefined>();
  const entity = EntityType.ACCOUNTING_DOCUMENT;

  useEffect(() => {
    if (items && items.length === 0) addNewLine();
  }, []);

  // set edit mode to false when user clicks outside table
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (tableRef.current && !tableRef.current.contains(event.target as Node)) {
        // clicked outside table
        handleChangeEditMode(false, undefined);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  /**
   * Loads currencies by selected date from modal
   */
  const fetchCurrenciesData = async (date: Date) => {
    startLoading();
    const response = await http.apiGet({
      url: `${apiUrl.CURRENCY_GET_LIST}?date=${format(new Date(date), dateSubmitFormat)}`,
    });
    if (response?.data) {
      if (!invoiceCurrency || !selectedItem) return;
      // calculate price in different currency to invoice´s currency
      const itemCurrency = response?.data.find((x: Currency) => x.code === selectedItem?.currency);
      const _item = {
        ...selectedItem,
        price: (selectedItem?.price * itemCurrency.rate) / invoiceCurrency.rate,
      };
      if (_item) handleSelectedItem(selectedIndex ?? items.length - 1, _item, true);
      stopLoading();
    }
    stopLoading();
  };

  /**
   * Opens modal table on line with selected index
   * entity - defines which table to open
   */
  const handleOpenModalTable = (entity: ModalEntityOption, selectedIndex: number) => {
    setSelectedIndex(selectedIndex);
    if (entity === ModalEntityOption.VAT_CODES) setIsTableOpen(true);
    if (entity === ModalEntityOption.ITEMS) setCodeBookItemsOpen(true);
  };

  /**
   * Closes modal table and resets index
   */
  const handleCloseModalTable = () => {
    setSelectedIndex(undefined);
    if (isTableOpen) setIsTableOpen(false);
    if (codeBookItemsOpen) setCodeBookItemsOpen(false);
  };

  /**
   * item input name changed
   */
  const onItemNameChange = (id: number, name: string) => {
    if (!name || !id) return;
    const newItems = [...items];
    const item = newItems.find((e) => e.id === id);
    if (!item) return;
    item.name = name;
    onItemsChange(newItems);
  };

  /**
   * item input code book changed
   */
  const onItemCodeBookItemCodeChange = (id: number, code?: string) => {
    if (!id) return;
    const newItems = [...items];
    const item = newItems.find((e) => e.id === id);
    if (!item) return;
    item.codeBookItemCode = code;
    onItemsChange(newItems);
  };
  /**
   * item input price changed
   */
  const onItemPriceChange = (id: number, e: any) => {
    const newItems = [...items];
    const item = newItems.find((e) => e.id === id);
    if (!item) return;
    item.price = +e;
    onItemsChange(newItems);
  };
  /**
   * item input quantity changed
   */
  const onItemQuantityChange = (id: number, e: any) => {
    const newItems = [...items];
    const item = newItems.find((e) => e.id === id);
    if (!item) return;
    item.quantity = +e;
    onItemsChange(newItems);
  };
  /**
   * item input VAT changed - by autofill item
   */
  const onItemVATChange = (id: number, e: any) => {
    const newItems = [...items];
    const item = newItems.find((x) => x.id === id);
    if (item && vatCodes) onItemVATCodeChange(item.id, e);
  };
  /**
   * item input VAT changed - by selection table
   */
  const onItemVATCodeChange = (id: number, e: any) => {
    const newItems = [...items];
    const item = newItems.find((x) => x.id === id);
    if (item) {
      item.vatCode = e.vatCode
        ? typeof e.vatCode === 'object'
          ? e.vatCode.code
          : e.vatCode
        : e.code && e.isVatCode
        ? e.code
        : undefined;
      item.vat = vatCodes?.find((x: VatCode) => x.code === item.vatCode)?.coefficientPercent;
      onItemsChange(newItems);
    }
    setSelectedIndex(undefined);
    setIsTableOpen(false);
  };
  /**
   * item input unit changed
   */
  const onItemUnitChange = (id: number, e: any) => {
    const newItems = [...items];
    const item = newItems.find((e) => e.id === id);
    if (!item) return;
    item.unit = e;
    onItemsChange(newItems);
  };

  /**
   * create new temp item
   */
  const addNewLine = () => {
    const newItems = [...items];
    const maxValue = newItems.length;
    newItems.push({
      id: maxValue + 1,
      name: '',
      codeBookItemCode: '',
      price: 0,
      quantity: 1,
      rate: '',
      vat: items[maxValue - 1]?.vat ? items[maxValue - 1].vat : 0,
      vatCode: items[maxValue - 1]?.vatCode ? items[maxValue - 1].vatCode : undefined,
      unit: '',
      currency: '',
      index: maxValue,
    });
    onItemsChange(newItems);
  };

  /**
   * remove item from table
   */
  const removeItem = (id: number) => {
    onItemsChange(items.filter((e) => e.id !== id));
  };

  /**
   * Item selected by previous selection item hint - fill values
   */
  const handleSelectedItem = (id: number, data: InvoiceItem, unifiedCurrency?: boolean) => {
    // if currency of item is not same as invoice currency
    // --> show modal with date input to select currency date date
    if (data.currency && data.currency !== invoiceCurrency.code && !unifiedCurrency) {
      setSelectedIndex(id);
      setSelectedItem(data);
      return setModalDiffCurrencyOpen(true);
    }
    if (data.name) onItemNameChange(id, data.name);
    if (data.code) {
      onItemCodeBookItemCodeChange(id, data.code);
    } else {
      onItemCodeBookItemCodeChange(id, undefined);
    }
    if (data.price) onItemPriceChange(id, data.price);
    if (data) onItemVATChange(id, data);
    if (data.unit) onItemUnitChange(id, data.unit);
    if (data.quantity) onItemQuantityChange(id, data.quantity);
  };

  /**
   * Selection of item that has different currency than invoice´s currency
   */
  const handleSelectItemDiffCurrency = () => {
    if (!itemCurrencyRatesDate) return;
    fetchCurrenciesData(itemCurrencyRatesDate);
    setModalDiffCurrencyOpen(false);
  };

  /**
   * Drag event ended --> save new indexes
   */
  const handleDragEnd = (result: any) => {
    if (!result.destination) return;
    const { source, destination } = result;
    const newRows = [...items];
    const [removed] = newRows.splice(source.index, 1);
    newRows.splice(destination.index, 0, removed);
    newRows.map((row: RowData, index: number) => (row.index = index));
    onItemsChange(newRows);
  };

  /**
   * If tab is presssed and current item is last then create new line and set focus to it
   */
  const handleKeyDown = (event: any, isLast: boolean) => {
    if (event.key === 'Tab' && isLast) {
      addNewLine();
    }
  };

  /**
   * Sets edit mode and index
   */
  const handleChangeEditMode = (isEdit: boolean, index?: number) => {
    setIsEdit(isEdit);
    setSelectedIndex(index);
  };

  /**
   * Check if column is not empty (for all items data)
   */
  const isColumnNotEmpty = (column: keyof RowData) => {
    return items.some(
      (item: RowData) =>
        item.price === null ||
        item.price === undefined ||
        item.name === '' ||
        (item[column] !== '' && item[column] !== null && item[column] !== undefined),
    );
  };

  /**
   * Creates info heading for codebook items hints
   */
  const getModalCodeBookItemsHintsHeading = () => {
    if (!itemHints) return;
    const anyCodeBookItem = itemHints.some((item) => item.isFromCodeBookItemList === true);
    if (!anyCodeBookItem) return;
    return {
      // always on first row if any item exists
      rowIdx: 0,
      heading: (
        <td className={`mr-3 border-t-2 pl-2`} colSpan={codeBookColumns().length}>
          <span className="w-full text-[0.7rem] text-gray-400">
            {t('page.invoice.code-book-items')}
          </span>
        </td>
      ),
    } as RowHeading;
  };

  /**
   * Creates info heading for previous items hints
   */
  const getModalPreviousHintsHeading = () => {
    if (!itemHints) return;
    const firstPreviousItemIndex = itemHints.findIndex(
      (item) => item.isFromCodeBookItemList === false,
    );
    if (firstPreviousItemIndex === -1) return;
    return {
      rowIdx: firstPreviousItemIndex,
      heading: (
        <td className={`mr-3 border-t-2 pl-2`} colSpan={codeBookColumns().length}>
          <span className="w-full text-[0.7rem] text-gray-400">
            {t('page.invoice.previously-used-items')}
          </span>
        </td>
      ),
    } as RowHeading;
  };

  /**
   * Creates array of headings for modal
   * @returns array of headings for modal
   */
  const getModalHintsHeadings = () => {
    const codeBookItemsHintsHeading = getModalCodeBookItemsHintsHeading();
    const previousHintsHeading = getModalPreviousHintsHeading();
    const headings = [];
    if (codeBookItemsHintsHeading) headings.push(codeBookItemsHintsHeading);
    if (previousHintsHeading) headings.push(previousHintsHeading);
    return headings;
  };

  const inputCss = `w-full`;

  useEffect(() => {
    if (!items) return;
    const totalColNum = 9;
    let columnNumber = totalColNum;
    if (!isColumnNotEmpty('unit')) columnNumber--;
    if (!isColumnNotEmpty('codeBookItemCode') || !codeBookItemsUseCode) columnNumber--;
    const widthPerColumn = 11.8;
    const nameColWidth = `${(totalColNum - columnNumber) * widthPerColumn + widthPerColumn}`;
    setColNameStyle(nameColWidth);
  }, [items]);

  if (!vatCodes) return <Loader />;
  return (
    <>
      <DragDropContext onDragEnd={handleDragEnd}>
        <table className="border border-gray-300 w-full text-xs" ref={tableRef}>
          <thead className="border border-gray-300">
            <tr className={`text-left [&>th]:px-1`}>
              <th style={{ width: colNameStyle + `%` }} className={`pl-1`}>
                {t('page.invoice.invoice-items.col-item', entity)}
              </th>
              {codeBookItemsUseCode && isColumnNotEmpty('codeBookItemCode') && (
                <th className="min-w-[65px]">
                  {t('page.invoice.invoice-items.col-code-book-items', entity)}
                </th>
              )}
              {isColumnNotEmpty('unit') && (
                <th>{t('page.invoice.invoice-items.col-unit', entity)}</th>
              )}
              <th className="text-right min-w-fit">
                {t('page.invoice.invoice-items.col-unit-price', entity)}
              </th>
              <th className="text-right w-[60px]">
                {t('page.invoice.invoice-items.col-quantity', entity)}
              </th>
              <th className="">{t('page.invoice.invoice-items.col-vat', entity)}</th>
              <th className="text-right">
                {t('page.invoice.invoice-items.col-total-price', entity)}
              </th>
              <th className="text-right">
                {t('page.invoice.invoice-items.col-total-price-vat', entity)}
              </th>
              {!isReadOnly && <th className="w-[32px]"></th>}
            </tr>
          </thead>
          <Droppable droppableId="rows">
            {(provided) => (
              <tbody className="text-sm" {...provided.droppableProps} ref={provided.innerRef}>
                {items.map((item, index: number) => (
                  <Draggable
                    key={item.id}
                    draggableId={item.index.toString()}
                    index={index}
                    isDragDisabled={!active}
                  >
                    {(provided) => (
                      <tr
                        key={item.id + '_' + index}
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        className={`[&>td]:px-1`}
                      >
                        <td
                          style={{ width: colNameStyle + `%` }}
                          className={`align-top`}
                          onKeyDownCapture={() => handleChangeEditMode(true, index)}
                          onFocus={() => handleChangeEditMode(true, index)}
                          onClickCapture={() => handleChangeEditMode(true, index)}
                          onBlurCapture={() => handleCloseModalTable()}
                        >
                          <div className="flex justify-between">
                            <div className="w-full">
                              <SelectAutocompleteTable
                                data={itemHints ?? []}
                                value={item.name}
                                labelKey="name"
                                valueKey="name"
                                placeholder={t('page.invoice.invoice-items.enter-name', entity)}
                                isReadOnly={isReadOnly}
                                onSelected={(e) => {
                                  handleSelectedItem(item.id, e);
                                  handleChangeEditMode(true, index + 1);
                                  setItemHints && setItemHints([]);
                                  addNewLine();
                                }}
                                onChange={(e) => onItemNameChange(item.id, e.name ? e.name : e)}
                                onQueryChange={async (e) => await onChangeNameQuery(e)}
                                className={`border-0 m-0 !pl-0.5`}
                                columns={codeBookColumns}
                                isFocused={selectedIndex === index}
                                aboveRowHeading={getModalHintsHeadings()}
                                // number of columns has changed - recalculate width of column
                                recalculate={colNameStyle}
                                isPrint={isPrint}
                              />
                            </div>
                            {codeBookItems && codeBookItems.length > 0 && (
                              <div className="mt-1">
                                <MagnifyingGlassPlusIcon
                                  className="w-3.5 h-3.5 m-0 cursor-pointer"
                                  onClickCapture={() =>
                                    handleOpenModalTable(ModalEntityOption.ITEMS, index)
                                  }
                                />
                              </div>
                            )}
                          </div>
                        </td>
                        {codeBookItemsUseCode && isColumnNotEmpty('codeBookItemCode') && (
                          <td
                            tabIndex={
                              codeBookItemsUseCode && isColumnNotEmpty('codeBookItemCode')
                                ? undefined
                                : -1
                            }
                            className="align-top"
                            onFocusCapture={() => handleChangeEditMode(true, index)}
                            onClickCapture={() => handleChangeEditMode(true, index)}
                          >
                            <input
                              className={inputCss}
                              type="text"
                              value={item.codeBookItemCode}
                              placeholder={
                                isPrint
                                  ? ''
                                  : t(
                                      'page.invoice.invoice-items.enter-code-book-item-code',
                                      entity,
                                    )
                              }
                              onChange={(e) =>
                                onItemCodeBookItemCodeChange(item.id, e.target.value)
                              }
                            />
                          </td>
                        )}
                        {isColumnNotEmpty('unit') && (
                          <td
                            tabIndex={isColumnNotEmpty('unit') ? undefined : -1}
                            className="align-top"
                            onFocusCapture={() => handleChangeEditMode(true, index)}
                            onClickCapture={() => handleChangeEditMode(true, index)}
                          >
                            <input
                              className={inputCss}
                              type="text"
                              value={item.unit}
                              placeholder={
                                isPrint ? '' : t('page.invoice.invoice-items.enter-unit', entity)
                              }
                              onChange={(e) => e && onItemUnitChange(item.id, e.target.value)}
                            />
                          </td>
                        )}
                        <td
                          className={`align-top`}
                          style={{
                            textAlign: 'right',
                          }}
                          onFocusCapture={() => {
                            handleChangeEditMode(true, index);
                          }}
                          onClickCapture={() => handleChangeEditMode(true, index)}
                        >
                          <input
                            type={'text'}
                            className={inputCss + ` text-right`}
                            style={{
                              display: !isEdit || selectedIndex !== index ? 'block' : 'none',
                            }}
                            placeholder={
                              isPrint ? '' : t('page.invoice.invoice-items.enter-price', entity)
                            }
                            value={
                              item.price !== null && item.price !== undefined
                                ? isPrint
                                  ? formatPrice(
                                      item.price,
                                      maxDecimalNumUnitPrice ?? 2,
                                      maxDecimalNumUnitPrice ?? 2,
                                    )
                                  : // no rounding for edit
                                    formatPrice(item.price, 0, 10)
                                : ''
                            }
                            onFocus={() => {
                              document.getElementById(`price_input_` + index)?.focus();
                            }}
                            readOnly
                          />
                          <input
                            id={`price_input_` + index}
                            type={'number'}
                            className={inputCss}
                            style={{
                              textAlign: 'right',
                              display: isEdit && selectedIndex === index ? 'block' : 'none',
                            }}
                            placeholder={
                              isPrint ? '' : t('page.invoice.invoice-items.enter-price', entity)
                            }
                            onChange={(e: any) => {
                              onItemPriceChange(item.id, e.target.value);
                            }}
                            value={item?.price || ''}
                            onFocus={(event) => event.target.select()}
                          />
                        </td>
                        <td
                          className="align-top"
                          onFocusCapture={() => handleChangeEditMode(true, index)}
                          onClickCapture={() => handleChangeEditMode(true, index)}
                        >
                          <input
                            type={'text'}
                            className={inputCss + ` text-right`}
                            style={{
                              display: !isEdit || selectedIndex !== index ? 'block' : 'none',
                            }}
                            value={item.quantity?.toLocaleString() || ''}
                            onFocus={() => {
                              document.getElementById(`quantity_input_` + index)?.focus();
                            }}
                            readOnly
                          />
                          <input
                            id={`quantity_input_` + index}
                            className={inputCss}
                            type="number"
                            pattern="[0-9]*"
                            style={{
                              textAlign: 'right',
                              display: isEdit && selectedIndex === index ? 'block' : 'none',
                            }}
                            min={0}
                            value={item.quantity || ''}
                            placeholder={
                              isPrint ? '' : t('page.invoice.invoice-items.enter-quantity', entity)
                            }
                            onChange={(e) => e && onItemQuantityChange(item.id, e.target.value)}
                            onFocus={(event) => event.target.select()}
                          />
                        </td>
                        <td className="align-top min-w-max">
                          <span
                            className="inline-flex whiteSpace-nowrap"
                            onKeyDown={(e) => handleKeyDown(e, index === items.length - 1)}
                            // onClick has different event on span
                            onClickCapture={() =>
                              handleOpenModalTable(ModalEntityOption.VAT_CODES, index)
                            }
                            onFocusCapture={() =>
                              handleOpenModalTable(ModalEntityOption.VAT_CODES, index)
                            }
                            onBlurCapture={() => handleCloseModalTable()}
                          >
                            <>
                              {!isReadOnly && item.vatCode && <div>{item.vatCode + ':'}</div>}
                              <SelectFilteredModalTable
                                data={vatCodes ?? []}
                                columns={columns}
                                open={isTableOpen && selectedIndex === index}
                                onClose={() => handleCloseModalTable()}
                                value={
                                  item.vat !== undefined && item.vat !== null ? item.vat + '' : ''
                                }
                                labelKey="code"
                                valueKey="code"
                                displayValue={'coefficientPercent'}
                                onSelected={(val) =>
                                  onItemVATCodeChange(item.id, { ...val, isVatCode: true })
                                }
                                onChange={(val) => onItemVATCodeChange(item.id, val)}
                                placeholder={t('page.invoice.invoice-items.enter-vat', entity)}
                                inputClassName={`w-[32px] text-right`}
                                fulltextSearch
                              />
                            </>
                          </span>
                        </td>
                        <td tabIndex={-1} className="text-right align-top whitespace-nowrap">
                          <span tabIndex={-1}>
                            {item.price && item.quantity
                              ? formatPrice(roundNumber(item.price * item.quantity, rounding))
                              : formatPrice(0)}
                          </span>
                        </td>
                        <td tabIndex={-1} className="text-right align-top whitespace-nowrap">
                          <span tabIndex={-1}>
                            {item.price && item.quantity
                              ? formatPrice(
                                  roundNumber(
                                    item.price *
                                      item.quantity *
                                      (item.vat ? item.vat / 100 + 1 : 1),
                                    rounding,
                                  ),
                                )
                              : formatPrice(0)}
                          </span>
                        </td>
                        {!isReadOnly && (
                          <td className="align-top pt-1 !px-0">
                            <div className="float-left">
                              <Bars3Icon
                                className="h-4 w-4 cursor-pointer hover:text-blue-500"
                                onMouseOver={() => setActive(true)}
                                onMouseOut={() => setActive(false)}
                              />
                            </div>
                            <div className="float-left w-[30%]">
                              <XMarkIcon
                                className="h-4 w-4 cursor-pointer hover:text-blue-500"
                                onClick={() => removeItem(item.id)}
                              />
                            </div>
                          </td>
                        )}
                        <td className="!px-0 !m-0 !w-0">
                          <SelectFilteredModalTable
                            data={codeBookItems ?? []}
                            columns={codeBookColumns}
                            open={codeBookItemsOpen && selectedIndex === index}
                            onClose={handleCloseModalTable}
                            labelKey="name"
                            valueKey="id"
                            showResult={false}
                            onSelected={(val) => handleSelectedItem(item.id, val)}
                            onChange={(val) => handleSelectedItem(item.id, val)}
                            fulltextSearch
                            inputClassName={`border-0`}
                            modalClassName={`left-[10%] right-[10%]`}
                          />
                        </td>
                      </tr>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </tbody>
            )}
          </Droppable>
        </table>
      </DragDropContext>
      {/* Modal for setting item currency rate date */}
      <Modal
        open={modalDiffCurrencyOpen}
        title={t('page.invoice.different-currency-info-headline')}
        className={`min-w-[15%]`}
        onClose={() => setModalDiffCurrencyOpen(false)}
      >
        <>
          <span className="w-max">{t('page.invoice.different-currency-info')}</span>
          <div className="mt-5 w-[50%]">
            <DateField
              className="border cursor-default rounded-lg bg-white py-2 pl-3 pr-5 text-left shadow-md focus:outline-none sm:text-sm"
              type="Date"
              title={t('page.invoice.input-currency-rates-date')}
              value={itemCurrencyRatesDate ? new Date(itemCurrencyRatesDate) : new Date()}
              handleChange={(e: Date | null) => {
                if (!e) return setItemCurrencyRatesDate(null);
                setItemCurrencyRatesDate(new Date(e));
              }}
              placeholder={t('page.invoice.input-currency-rates-date')}
              isRequired
            />
          </div>
          <Button
            className="mt-5"
            title={t('general.btn-select')}
            onClick={handleSelectItemDiffCurrency}
          />
        </>
      </Modal>
      {!isReadOnly && (
        <Button
          id="itm-add-new-row"
          title={t('page.invoice.invoice-items.btn-add-new-row', entity)}
          onClick={addNewLine}
          icon={<PlusIcon className="w-4 h-4 mr-2 bg-orange-500 rounded-lg text-white" />}
          className={`w-full px-5 group relative flex justify-center border border-black border-dashed bg-[#fff] text-black text-sm font-medium rounded-md text-white hover:bg-orange-200 focus:outline-none focus:ring-2 mt-2`}
        />
      )}
    </>
  );
}
